import React, { useState } from 'react';
import { CaretDown, CaretUp, UsersThree, X, HardHat, ClockCountdown } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { schedulingAPI } from '../../api/api';
import { CrewSuggestions } from './CrewSuggestions';

const fmtDate = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const SlipForm = ({ scheduleId, phaseId, onDone }) => {
  const [days, setDays] = useState(2);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await schedulingAPI.slip(scheduleId, phaseId, { days: Number(days), reason: reason.trim() || null });
      const n = data.shifted_phases.length;
      toast.success(`Delay recorded — ${n} downstream phase${n === 1 ? '' : 's'} rescheduled, ${data.notified} crew member${data.notified === 1 ? '' : 's'} notified`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record delay');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-xl p-3.5" data-testid="slip-form">
      <p className="text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-2">Report a delay</p>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={days} onChange={(e) => setDays(e.target.value)}
          className="h-9 px-2.5 rounded-lg border-2 border-amber-300 dark:border-amber-700 bg-white dark:bg-neutral-900 text-sm font-bold dark:text-white" data-testid="slip-days-select">
          {[1, 2, 3, 4, 5, 7, 10, 14].map((n) => <option key={n} value={n}>+{n} day{n > 1 ? 's' : ''}</option>)}
        </select>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional) — e.g. inspection pushed"
          className="flex-1 min-w-[160px] h-9 px-3 rounded-lg border-2 border-amber-300 dark:border-amber-700 bg-white dark:bg-neutral-900 text-sm dark:text-white" data-testid="slip-reason-input" />
        <button onClick={submit} disabled={saving}
          className="h-9 px-4 rounded-full bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-xs font-bold" data-testid="slip-confirm-btn">
          {saving ? 'Updating...' : 'Shift schedule'}
        </button>
      </div>
      <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-2">All downstream phases move automatically and assigned crew get re-notified.</p>
    </div>
  );
};

export const PhaseCard = ({ scheduleId, phase, index, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [slipOpen, setSlipOpen] = useState(false);
  const assignedCount = (phase.assigned_worker_ids || []).length;
  const staffed = assignedCount >= (phase.workers_needed || 1);

  const unassign = async (workerId) => {
    try {
      await schedulingAPI.unassign(scheduleId, phase.phase_id, workerId);
      toast.success('Worker removed from phase');
      onChanged();
    } catch {
      toast.error('Could not remove worker');
    }
  };

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-[#0F172A] dark:border-neutral-700 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#EA580C] overflow-hidden" data-testid={`phase-card-${index}`}>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#EA580C] text-white flex items-center justify-center font-black text-sm shrink-0">
            {index + 1}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                {phase.name}
                {phase.status === 'delayed' && (
                  <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/50 rounded-full px-2 py-0.5" data-testid={`phase-delayed-badge-${index}`}>
                    <ClockCountdown size={11} weight="fill" /> Delayed +{phase.slipped_days}d
                  </span>
                )}
              </h3>
              <span className="text-xs font-bold text-neutral-600 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-700 rounded-full px-2.5 py-1 shrink-0" data-testid={`phase-dates-${index}`}>
                {fmtDate(phase.start_date)} → {fmtDate(phase.end_date)} · {phase.duration_days}d
              </span>
            </div>
            {phase.description && <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{phase.description}</p>}
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              {(phase.trade_skills || []).map((s) => (
                <span key={s} className="text-[10px] font-bold uppercase text-[#C2410C] bg-orange-50 dark:bg-orange-900/30 dark:text-orange-300 rounded-full px-2 py-0.5">{s}</span>
              ))}
              <span className={`flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 ${staffed ? 'text-emerald-700 bg-emerald-50 dark:bg-emerald-900/40 dark:text-emerald-300' : 'text-neutral-600 bg-neutral-100 dark:bg-neutral-700 dark:text-neutral-300'}`}>
                <UsersThree size={12} weight="bold" /> {assignedCount}/{phase.workers_needed} crew
              </span>
            </div>
            {assignedCount > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap mt-2" data-testid={`phase-assigned-${index}`}>
                {(phase.assigned_workers || []).map((w) => (
                  <span key={w.id} className="flex items-center gap-1 text-xs font-semibold text-emerald-800 dark:text-emerald-200 bg-emerald-100 dark:bg-emerald-900/50 rounded-full pl-2.5 pr-1 py-1">
                    <HardHat size={13} weight="fill" /> {w.name}
                    <button onClick={() => unassign(w.id)} className="w-4 h-4 rounded-full hover:bg-emerald-200 dark:hover:bg-emerald-800 flex items-center justify-center" data-testid={`unassign-${w.id}`}>
                      <X size={10} weight="bold" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => setOpen(!open)}
            className="flex-1 h-9 rounded-full border-2 border-[#EA580C] text-[#EA580C] hover:bg-orange-50 dark:hover:bg-orange-900/20 text-xs font-bold flex items-center justify-center gap-1.5 transition-colors"
            data-testid={`suggest-crew-btn-${index}`}
          >
            {open ? <CaretUp size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />}
            {open ? 'Hide crew suggestions' : 'Suggest crew (AI match)'}
          </button>
          <button
            onClick={() => setSlipOpen(!slipOpen)}
            className={`h-9 px-3.5 rounded-full border-2 text-xs font-bold flex items-center gap-1.5 transition-colors ${slipOpen ? 'border-amber-500 bg-amber-500 text-white' : 'border-amber-400 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20'}`}
            data-testid={`report-delay-btn-${index}`}
          >
            <ClockCountdown size={13} weight="bold" /> Delay
          </button>
        </div>
        {slipOpen && <SlipForm scheduleId={scheduleId} phaseId={phase.phase_id} onDone={() => { setSlipOpen(false); onChanged(); }} />}
        {open && <CrewSuggestions scheduleId={scheduleId} phase={phase} onAssigned={onChanged} />}
      </div>
    </div>
  );
};
